/**
 * Security Context Provider
 * 
 * Provides global access to security state (user login status, route security, etc.)
 * across the application. This context is updated by SecurityVerifier on each route navigation.
 * 
 * Usage:
 *   const { isUserLoggedIn, userInfo, currentRouteTitle } = useSecurityContext()
 */

import { createContext, useContext, useState, useEffect } from 'react'
import type { ReactNode } from 'react'
import type { GlobalContext } from '@/security/routeSecurityVerifier'
import { AppLogger } from '@/lib/logger'

const log = AppLogger.getLogger(import.meta)

// ============================================================================
// Module-level state and update callbacks
// ============================================================================

// Store the current context in module-level state
let currentGlobalContext: GlobalContext = {
  isUserLoggedIn: false,
  isSecuredRoute: true,
  currentRouteTitle: '',
}

// Registry of update callbacks from SecurityProvider instances
const updateCallbacks = new Set<(context: GlobalContext) => void>()

/**
 * Update the global security context (called by SecurityVerifier)
 * This notifies all SecurityProvider instances via callbacks
 */
export function updateSecurityContext(context: GlobalContext): void {
  currentGlobalContext = context
  
  // Notify all registered providers
  updateCallbacks.forEach((callback) => {
    try {
      callback(context)
    } catch (error) {
      log.error('Error updating security context:', error)
    }
  })
  
  // Also dispatch event for any other listeners
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('security-context-updated', { detail: context }))
  }
}

/**
 * Get the current global security context
 */
export function getSecurityContext(): GlobalContext {
  return currentGlobalContext
}

// ============================================================================
// Context
// ============================================================================

const SecurityContext = createContext<GlobalContext | undefined>(undefined)

// ============================================================================
// Provider
// ============================================================================

interface SecurityProviderProps {
  children: ReactNode
}

export function SecurityProvider({ children }: SecurityProviderProps) {
  // Initialize with current module-level state
  const [globalContext, setGlobalContext] = useState<GlobalContext>(() => getSecurityContext())

  // Register update callback (no polling - event-driven only)
  useEffect(() => {
    const handleUpdate = (context: GlobalContext) => {
      setGlobalContext(context)
    }

    // Register this provider's update callback
    updateCallbacks.add(handleUpdate)

    // Also listen for window events as a fallback
    const handleWindowEvent = (event: Event) => {
      const customEvent = event as CustomEvent<GlobalContext>
      if (customEvent.detail) {
        setGlobalContext(customEvent.detail)
      }
    }

    window.addEventListener('security-context-updated', handleWindowEvent)

    // Cleanup
    return () => {
      updateCallbacks.delete(handleUpdate)
      window.removeEventListener('security-context-updated', handleWindowEvent)
    }
  }, [])

  return (
    <SecurityContext.Provider value={globalContext}>
      {children}
    </SecurityContext.Provider>
  )
}

// ============================================================================
// Hook
// ============================================================================

export function useSecurityContext(): GlobalContext {
  const context = useContext(SecurityContext)
  
  if (context === undefined) {
    throw new Error('useSecurityContext must be used within a SecurityProvider')
  }
  
  return context
}

// ============================================================================
// Exports
// ============================================================================

// GlobalContext type is exported from @/security/routeSecurityVerifier

